import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiRefreshCw,
  FiShield,
  FiUser,
  FiMessageSquare,
  FiCheck,
  FiX,
  FiUserPlus,
  FiInfo,
  FiX as FiClose,
  FiLayers,
  FiMapPin,
  FiFileText,
  FiHash,
  FiClock,
  FiSearch,
  FiBriefcase,
  FiKey,
  FiEye,
  FiPackage
} from 'react-icons/fi';
import { AnimatePresence, motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { legalReviewsApi, assetsApi, assetBulkRegistrationsApi, ipfsApi } from '../../api/smartTokenizerApi';
import { formatEnumLabel, formatSectorLabel, mapAssetToFrontend } from '../../api/smartTokenizerMappers';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import { getStoredSession } from '../../api/authStorage';
import {
  assetDocumentRowKey,
  buildAssetDocumentPreviewBlob,
  openAssetDocumentObjectUrl
} from '../../utils/assetDocumentPreview';

const pick = (obj, ...keys) => {
  if (!obj) return null;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return null;
};

const nestedAsset = (review) => review?.asset ?? review?.Asset ?? null;
const nestedBulkRegistration = (review) => review?.bulkRegistration ?? review?.BulkRegistration ?? null;

const assetTitle = (a) => pick(a, 'title', 'Title') || 'Untitled asset';
const assetSectorRaw = (a) => pick(a, 'sector', 'Sector');
const assetNumber = (a) => pick(a, 'assetNumber', 'AssetNumber');
const assetCountry = (a) => pick(a, 'country', 'Country');
const assetStatus = (a) => pick(a, 'status', 'Status');
const assetClassName = (a) => {
  const ac = a?.assetClass ?? a?.AssetClass;
  return pick(ac, 'name', 'Name');
};
const tokenizerOrg = (a) => {
  const t = a?.authorizedTokenizer ?? a?.AuthorizedTokenizer;
  return pick(t, 'organizationName', 'OrganizationName');
};
const documentsOf = (a) => a?.documents ?? a?.Documents ?? [];

const bulkRegistrationTitle = (br) => pick(br, 'title', 'Title') || 'Bulk registration';
const bulkAssetsList = (br) => br?.assets ?? br?.Assets ?? [];
const bulkDocCount = (br) => bulkAssetsList(br).reduce((sum, a) => sum + documentsOf(a).length, 0);
/** Single-asset review has assetId; bulk batch review links bulkRegistrationId only. */
const isBulkLegalReview = (r) => Boolean(reviewBulkRegistrationId(r)) && !reviewAssetId(r);

const reviewAssetId = (r) => pick(r, 'assetId', 'AssetId');
const reviewBulkRegistrationId = (r) => pick(r, 'bulkRegistrationId', 'BulkRegistrationId');
const reviewIdStr = (r) => String(pick(r, 'id', 'Id') ?? '');
const reviewCreatedAt = (r) => pick(r, 'createdAtUtc', 'CreatedAtUtc');

const shortGuid = (id) => {
  const s = String(id || '');
  if (s.length <= 12) return s;
  return `${s.slice(0, 8)}…${s.slice(-4)}`;
};

const normalizeAssignableReviewer = (r) => ({
  userId: String(r.userId ?? r.UserId ?? ''),
  email: r.email ?? r.Email ?? '',
  fullName: r.fullName ?? r.FullName ?? '',
  organizationName: r.organizationName ?? r.OrganizationName ?? ''
});

const reviewerPrimaryLabel = (r) => {
  const name = (r.fullName || '').trim();
  const email = (r.email || '').trim();
  if (name && email) return `${name} (${email})`;
  return name || email || r.userId;
};

function formatQueueTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
  });
}

/** Human-readable review status + color tone for UI */
function getReviewStatusMeta(review) {
  const raw = review?.status ?? review?.Status;
  const n = typeof raw === 'number' ? raw : null;
  const s = String(raw ?? '');
  if (n === 1 || /pendingassignment/i.test(s)) {
    return { tone: 'pending', label: 'Awaiting assignment', accent: '#fbbf24' };
  }
  if (n === 2 || /inreview/i.test(s)) {
    return { tone: 'inreview', label: 'In review', accent: '#60a5fa' };
  }
  if (n === 3 || /^approved$/i.test(s)) {
    return { tone: 'approved', label: 'Approved', accent: '#4ade80' };
  }
  if (n === 4 || /reject/i.test(s)) {
    return { tone: 'rejected', label: 'Rejected', accent: '#f87171' };
  }
  return { tone: 'default', label: formatEnumLabel(raw) || '—', accent: '#38bdf8' };
}

/** Stable tab key for queue filtering (aligned with getReviewStatusMeta tones). */
function getReviewStatusTabKey(review) {
  const meta = getReviewStatusMeta(review);
  if (meta.tone === 'pending') return 'pending';
  if (meta.tone === 'inreview') return 'inreview';
  if (meta.tone === 'approved') return 'approved';
  if (meta.tone === 'rejected') return 'rejected';
  return 'other';
}

const Page = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a3e 100%);
  color: #fff;
`;

const Hero = styled.div`
  margin-bottom: 2rem;

  h1 {
    font-size: 2rem;
    font-weight: 800;
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 0.5rem;
  }

  p {
    color: #a0a0a0;
    line-height: 1.6;
    max-width: 720px;
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.2rem;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => (p.$variant === 'secondary' ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #4facfe, #00f2fe)')};
  color: #fff;
  border: ${(p) => (p.$variant === 'secondary' ? '1px solid rgba(255,255,255,0.2)' : 'none')};

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Notice = styled.div`
  background: rgba(79, 172, 254, 0.12);
  border: 1px solid rgba(79, 172, 254, 0.35);
  color: #cfe9ff;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  line-height: 1.5;
  font-size: 0.95rem;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.25rem;
  min-height: 280px;
`;

const QueueScroll = styled.div`
  max-height: min(70vh, 720px);
  overflow-y: auto;
  padding-right: 0.25rem;
  margin-bottom: -0.25rem;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.25);
    border-radius: 4px;
  }
`;

const QueueTabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.85rem;
`;

const QueueTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.7rem;
  border-radius: 999px;
  border: 1px solid
    ${(p) => (p.$active ? 'rgba(79, 172, 254, 0.5)' : 'rgba(255, 255, 255, 0.1)')};
  background: ${(p) => (p.$active ? 'rgba(79, 172, 254, 0.18)' : 'rgba(0, 0, 0, 0.22)')};
  color: ${(p) => (p.$active ? '#e0f2fe' : '#94a3b8')};
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;

  &:hover {
    border-color: rgba(79, 172, 254, 0.35);
    color: ${(p) => (p.$active ? '#e0f2fe' : '#cbd5e1')};
  }
`;

const QueueTabCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.15rem;
  padding: 0.1rem 0.35rem;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 800;
  background: ${(p) => (p.$active ? 'rgba(0, 0, 0, 0.25)' : 'rgba(255, 255, 255, 0.08)')};
  color: ${(p) => (p.$active ? '#fff' : '#a0a0a0')};
`;

const SearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.2);
  color: #a0a0a0;

  svg {
    flex-shrink: 0;
  }

  input {
    flex: 1;
    border: none;
    background: transparent;
    color: #fff;
    font-size: 0.9rem;
    outline: none;
    min-width: 0;

    &::placeholder {
      color: #6b7280;
    }
  }
`;

const QueueCardRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0.6rem;
  margin-bottom: 0.85rem;
`;

const QueueCard = styled.button`
  position: relative;
  flex: 1;
  text-align: left;
  padding: 0;
  border-radius: 14px;
  border: 1px solid ${(p) => (p.$active ? 'rgba(79, 172, 254, 0.55)' : 'rgba(255, 255, 255, 0.08)')};
  background: ${(p) => (p.$active ? 'rgba(79, 172, 254, 0.12)' : 'rgba(255, 255, 255, 0.03)')};
  color: #fff;
  cursor: pointer;
  font: inherit;
  overflow: hidden;
  transition: border-color 0.2s, background 0.2s, transform 0.15s;

  &:hover {
    border-color: rgba(79, 172, 254, 0.35);
    background: rgba(255, 255, 255, 0.05);
    transform: translateY(-1px);
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${(p) => p.$accent || '#38bdf8'};
  }
`;

const CardInner = styled.div`
  padding: 1rem 1rem 0.85rem 1.2rem;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.65rem;
`;

const CardTitle = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
  color: #f8fafc;
  letter-spacing: -0.01em;
`;

const StatusBadge = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  ${(p) => {
    switch (p.$tone) {
      case 'pending':
        return 'background: rgba(251, 191, 36, 0.14); color: #fcd34d; border: 1px solid rgba(251, 191, 36, 0.4);';
      case 'inreview':
        return 'background: rgba(59, 130, 246, 0.18); color: #93c5fd; border: 1px solid rgba(59, 130, 246, 0.45);';
      case 'approved':
        return 'background: rgba(34, 197, 94, 0.16); color: #86efac; border: 1px solid rgba(34, 197, 94, 0.4);';
      case 'rejected':
        return 'background: rgba(239, 68, 68, 0.15); color: #fca5a5; border: 1px solid rgba(239, 68, 68, 0.4);';
      default:
        return 'background: rgba(79, 172, 254, 0.15); color: #9bd6ff; border: 1px solid rgba(79, 172, 254, 0.35);';
    }
  }}
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.65rem;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.28rem 0.55rem;
  border-radius: 8px;
  font-size: 0.78rem;
  color: #cbd5e1;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  max-width: 100%;

  svg {
    flex-shrink: 0;
    opacity: 0.85;
  }
`;

const CardFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 1rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 0.75rem;
  color: #94a3b8;
`;

const FooterHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-weight: 500;
  color: #64748b;
`;

const IconOnlyButton = styled.button`
  flex-shrink: 0;
  width: 46px;
  min-height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  border: 1px solid rgba(79, 172, 254, 0.45);
  background: rgba(79, 172, 254, 0.1);
  color: #9bd6ff;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(79, 172, 254, 0.22);
  }
`;

const EmptyQueue = styled.div`
  text-align: center;
  padding: 2.5rem 1.5rem;
  border-radius: 14px;
  border: 1px dashed rgba(79, 172, 254, 0.25);
  background: rgba(79, 172, 254, 0.04);
  color: #94a3b8;
  line-height: 1.6;

  strong {
    display: block;
    color: #e2e8f0;
    font-size: 1.05rem;
    margin-bottom: 0.35rem;
  }
`;

const Field = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    font-size: 0.85rem;
    color: #a0a0a0;
    margin-bottom: 0.35rem;
  }

  input,
  textarea {
    width: 100%;
    padding: 0.65rem;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(15, 15, 35, 0.8);
    color: #fff;
    font-size: 0.9rem;
  }

  textarea {
    min-height: 80px;
    resize: vertical;
  }
`;

const ReviewerPickerWrap = styled.div`
  position: relative;
`;

const ReviewerDropdown = styled.ul`
  list-style: none;
  margin: 0.35rem 0 0;
  padding: 0.35rem 0;
  position: absolute;
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 220px;
  overflow-y: auto;
  border-radius: 8px;
  border: 1px solid rgba(79, 172, 254, 0.35);
  background: rgba(18, 18, 40, 0.98);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.45);
`;

const ReviewerOption = styled.li`
  padding: 0.55rem 0.75rem;
  cursor: pointer;
  font-size: 0.88rem;
  line-height: 1.35;
  color: #e2e8f0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }

  &:hover,
  &[data-active='true'] {
    background: rgba(79, 172, 254, 0.18);
  }

  .name {
    font-weight: 600;
    color: #f8fafc;
  }

  .sub {
    font-size: 0.78rem;
    color: #94a3b8;
    margin-top: 0.15rem;
  }
`;

const ReviewerHint = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.78rem;
  color: #64748b;
`;

const CommentList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1rem 0;
  max-height: 200px;
  overflow-y: auto;

  li {
    padding: 0.65rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    font-size: 0.9rem;
    color: #e0e0e0;
  }

  .meta {
    font-size: 0.75rem;
    color: #a0a0a0;
    margin-top: 0.35rem;
  }
`;

const StatusPill = styled.span`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: rgba(79, 172, 254, 0.15);
  color: #9bd6ff;
  border: 1px solid rgba(79, 172, 254, 0.35);
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1.5rem;
`;

const ModalCard = styled(motion.div)`
  background: linear-gradient(145deg, #151528 0%, #1a1a38 100%);
  border: 1px solid rgba(79, 172, 254, 0.25);
  border-radius: 16px;
  max-width: 720px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.45);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  h2 {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
  }
`;

const ModalBody = styled.div`
  padding: 1.25rem;
  overflow-y: auto;
`;

const DetailGrid = styled.dl`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 0.5rem 1rem;
  margin: 0 0 1.25rem 0;
  font-size: 0.9rem;

  dt {
    color: #a0a0a0;
    margin: 0;
  }

  dd {
    margin: 0;
    color: #f0f0f0;
    word-break: break-word;
  }
`;

const JsonBlock = styled.pre`
  margin: 0;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.35);
  border-radius: 8px;
  font-size: 0.75rem;
  line-height: 1.45;
  overflow-x: auto;
  color: #c8e6c9;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const DocTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  th,
  td {
    text-align: left;
    padding: 0.5rem 0.4rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  th {
    color: #a0a0a0;
    font-weight: 600;
  }

  td.doc-actions {
    white-space: nowrap;
    width: 1%;
  }
`;

const DocViewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.65rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #e0f2fe;
  background: rgba(56, 189, 248, 0.15);
  border: 1px solid rgba(56, 189, 248, 0.35);
  border-radius: 6px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(56, 189, 248, 0.28);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  svg {
    flex-shrink: 0;
  }
`;

function BulkRegistrationReviewModal({ bulkModalState, onClose, onOpenMemberAsset }) {
  const { open, loading, error, bulk, reviewSnapshot } = bulkModalState;
  const members = bulk ? bulkAssetsList(bulk) : [];
  const bulkId = bulk ? pick(bulk, 'id', 'Id') : null;

  return (
    <AnimatePresence>
      {open && (
        <ModalOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
          <ModalCard
            initial={{ scale: 0.94, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.94, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 820 }}
          >
            <ModalHeader>
              <h2>Bulk registration</h2>
              <IconOnlyButton type="button" onClick={onClose} aria-label="Close">
                <FiClose />
              </IconOnlyButton>
            </ModalHeader>
            <ModalBody>
              {loading && <p style={{ color: '#a0a0a0' }}>Loading bulk registration…</p>}
              {error && !loading && <p style={{ color: '#ff8a8a' }}>{error}</p>}
              {!loading && !error && bulk && (
                <>
                  <DetailGrid>
                    <dt>Title</dt>
                    <dd>{bulkRegistrationTitle(bulk)}</dd>
                    <dt>Bulk registration ID</dt>
                    <dd style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{bulkId || '—'}</dd>
                    <dt>Status</dt>
                    <dd>{formatEnumLabel(pick(bulk, 'status', 'Status'))}</dd>
                    <dt>Members</dt>
                    <dd>{members.length}</dd>
                    <dt>Documents (all members)</dt>
                    <dd>{bulkDocCount(bulk)}</dd>
                  </DetailGrid>

                  {reviewSnapshot && (
                    <>
                      <h3 style={{ fontSize: '1rem', margin: '0 0 0.75rem', color: '#9bd6ff' }}>Legal review (snapshot)</h3>
                      <DetailGrid>
                        <dt>Review status</dt>
                        <dd>{formatEnumLabel(reviewSnapshot.status)}</dd>
                        <dt>Assigned reviewer</dt>
                        <dd style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {reviewSnapshot.assignedReviewerId || reviewSnapshot.AssignedReviewerId || '—'}
                        </dd>
                        <dt>Decision notes</dt>
                        <dd>{reviewSnapshot.decisionNotes || reviewSnapshot.DecisionNotes || '—'}</dd>
                      </DetailGrid>
                    </>
                  )}

                  <h3 style={{ fontSize: '1rem', margin: '1rem 0 0.5rem', color: '#9bd6ff' }}>Member assets</h3>
                  {members.length === 0 ? (
                    <p style={{ color: '#a0a0a0', fontSize: '0.9rem' }}>No member assets in this response.</p>
                  ) : (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {members.map((m, idx) => {
                        const mid = pick(m, 'id', 'Id');
                        const key = mid ? String(mid) : `member-${idx}`;
                        return (
                          <li
                            key={key}
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              gap: '0.65rem',
                              padding: '0.65rem 0',
                              borderBottom: '1px solid rgba(255,255,255,0.08)'
                            }}
                          >
                            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                              <div style={{ fontWeight: 600, color: '#e2e8f0' }}>{assetTitle(m)}</div>
                              <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.72rem', color: '#94a3b8', marginTop: '0.2rem' }}>
                                {mid ? shortGuid(mid) : '—'}
                              </div>
                            </div>
                            <Button
                              type="button"
                              $variant="secondary"
                              disabled={!mid}
                              onClick={() => onOpenMemberAsset(mid, reviewSnapshot)}
                            >
                              <FiEye />
                              Full asset info
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              )}
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
}

const LegalReviewCenter = () => {
  const { canAccessLegalReviewApi, canAssignOrDecideLegalReview, isAuditor } = useTokenIssuer();
  const [reviews, setReviews] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [assignReviewerId, setAssignReviewerId] = useState('');
  const [assignReviewerQuery, setAssignReviewerQuery] = useState('');
  const [assignReviewerMenuOpen, setAssignReviewerMenuOpen] = useState(false);
  const [assignableReviewers, setAssignableReviewers] = useState([]);
  const [reviewersLoading, setReviewersLoading] = useState(false);
  const reviewerPickerRef = useRef(null);
  const [decisionNotes, setDecisionNotes] = useState('');
  const [newComment, setNewComment] = useState('');

  const [assetModal, setAssetModal] = useState({
    open: false,
    loading: false,
    error: null,
    mapped: null,
    reviewSnapshot: null
  });
  const [bulkModal, setBulkModal] = useState({
    open: false,
    loading: false,
    error: null,
    bulk: null,
    reviewSnapshot: null
  });
  const [queueFilter, setQueueFilter] = useState('');
  const [queueStatusTab, setQueueStatusTab] = useState('all');
  const [openingDocumentKey, setOpeningDocumentKey] = useState(null);

  const session = getStoredSession();
  const signedIn = Boolean(session?.accessToken);

  const loadList = useCallback(async () => {
    if (!signedIn || !canAccessLegalReviewApi) {
      return;
    }

    setLoading(true);
    try {
      const { data } = await legalReviewsApi.list();
      setReviews(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to load legal reviews');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [canAccessLegalReviewApi, signedIn]);

  const loadDetail = useCallback(
    async (id) => {
      if (!id || !canAccessLegalReviewApi) {
        return;
      }

      try {
        const { data } = await legalReviewsApi.getById(id);
        setDetail(data);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to load review detail');
        setDetail(null);
      }
    },
    [canAccessLegalReviewApi]
  );

  const openLegalDocument = useCallback(async (doc) => {
    const cid = String(doc?.ipfsCid ?? doc?.IpfsCid ?? '').trim();
    const rowKey = assetDocumentRowKey(doc);
    if (!cid) {
      toast.error('No IPFS CID for this document.');
      return;
    }
    setOpeningDocumentKey(rowKey);
    try {
      const response = await ipfsApi.downloadByCid(cid);
      const blob = await buildAssetDocumentPreviewBlob(response, doc);
      const url = URL.createObjectURL(blob);
      const opened = openAssetDocumentObjectUrl(url, blob.type || 'application/octet-stream', doc);
      if (!opened) {
        toast.error('Pop-up blocked. Allow pop-ups to view the document.');
        URL.revokeObjectURL(url);
        return;
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 600000);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Could not load document');
    } finally {
      setOpeningDocumentKey(null);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (!signedIn || !canAssignOrDecideLegalReview) {
      return;
    }

    let cancelled = false;
    (async () => {
      setReviewersLoading(true);
      try {
        const { data } = await legalReviewsApi.assignableReviewers();
        if (cancelled) return;
        const rows = Array.isArray(data) ? data.map(normalizeAssignableReviewer).filter((x) => x.userId) : [];
        setAssignableReviewers(rows);
      } catch (error) {
        if (!cancelled) {
          setAssignableReviewers([]);
          toast.error(error.response?.data?.message || error.message || 'Failed to load assignable reviewers');
        }
      } finally {
        if (!cancelled) setReviewersLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [signedIn, canAssignOrDecideLegalReview]);

  useEffect(() => {
    if (selectedId) {
      loadDetail(selectedId);
    } else {
      setDetail(null);
    }
  }, [selectedId, loadDetail]);

  useEffect(() => {
    setAssignReviewerId('');
    setAssignReviewerQuery('');
    setAssignReviewerMenuOpen(false);
  }, [selectedId]);

  useEffect(() => {
    if (!assignReviewerMenuOpen) return undefined;
    const onDocMouseDown = (e) => {
      const el = reviewerPickerRef.current;
      if (el && !el.contains(e.target)) {
        setAssignReviewerMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [assignReviewerMenuOpen]);

  const queueStatusCounts = useMemo(() => {
    const c = { all: reviews.length, pending: 0, inreview: 0, approved: 0, rejected: 0, other: 0 };
    for (const r of reviews) {
      c[getReviewStatusTabKey(r)] += 1;
    }
    return c;
  }, [reviews]);

  const reviewsForStatusTab = useMemo(() => {
    if (queueStatusTab === 'all') return reviews;
    return reviews.filter((r) => getReviewStatusTabKey(r) === queueStatusTab);
  }, [reviews, queueStatusTab]);

  const filteredReviews = useMemo(() => {
    const q = queueFilter.trim().toLowerCase();
    if (!q) return reviewsForStatusTab;
    return reviewsForStatusTab.filter((r) => {
      const a = nestedAsset(r);
      const br = nestedBulkRegistration(r);
      const brFirst = br ? bulkAssetsList(br)[0] : null;
      const chipA = a || brFirst;
      const title = (a ? assetTitle(a) : br ? bulkRegistrationTitle(br) : '').toLowerCase();
      const sector = (chipA ? formatSectorLabel(assetSectorRaw(chipA)) : br ? formatSectorLabel(br.sector ?? br.Sector) : '')
        .toLowerCase();
      const cls = (chipA ? assetClassName(chipA) : '')?.toLowerCase() || '';
      const tok = (chipA ? tokenizerOrg(chipA) : '')?.toLowerCase() || '';
      const num = (chipA ? assetNumber(chipA) : '')?.toLowerCase() || '';
      const country = (chipA ? assetCountry(chipA) : br ? String(pick(br, 'country', 'Country') || '').toLowerCase() : '')
        .toLowerCase();
      const id = reviewIdStr(r).toLowerCase();
      const aid = (reviewAssetId(r) || '').toLowerCase();
      const bid = (reviewBulkRegistrationId(r) || '').toLowerCase();
      return (
        title.includes(q) ||
        sector.includes(q) ||
        cls.includes(q) ||
        tok.includes(q) ||
        num.includes(q) ||
        country.includes(q) ||
        id.includes(q) ||
        aid.includes(q) ||
        bid.includes(q)
      );
    });
  }, [reviewsForStatusTab, queueFilter]);

  useEffect(() => {
    if (!selectedId) return;
    const idStr = String(selectedId);
    if (!filteredReviews.some((r) => reviewIdStr(r) === idStr)) {
      setSelectedId(null);
    }
  }, [filteredReviews, selectedId]);

  const queueStatusTabs = useMemo(() => {
    const base = [
      { id: 'all', label: 'All' },
      { id: 'pending', label: 'Awaiting assignment' },
      { id: 'inreview', label: 'In review' },
      { id: 'approved', label: 'Approved' },
      { id: 'rejected', label: 'Rejected' }
    ];
    if (queueStatusCounts.other > 0) {
      base.push({ id: 'other', label: 'Other' });
    }
    return base;
  }, [queueStatusCounts.other]);

  const filteredAssignableReviewers = useMemo(() => {
    const q = assignReviewerQuery.trim().toLowerCase();
    if (!q) return assignableReviewers;
    return assignableReviewers.filter((r) => {
      return (
        r.fullName.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        r.organizationName.toLowerCase().includes(q) ||
        r.userId.toLowerCase().includes(q)
      );
    });
  }, [assignableReviewers, assignReviewerQuery]);

  const selectedSummary = reviews.find((r) => reviewIdStr(r) === String(selectedId));
  const selectedAsset = nestedAsset(selectedSummary) || nestedAsset(detail);
  const selectedBulk =
    nestedBulkRegistration(detail) || nestedBulkRegistration(selectedSummary);

  const openAssetFullInfo = async (assetId, reviewSnapshot = null) => {
    if (!assetId) return;
    setAssetModal({ open: true, loading: true, error: null, mapped: null, reviewSnapshot });
    try {
      const { data } = await assetsApi.getById(assetId);
      setAssetModal({
        open: true,
        loading: false,
        error: null,
        mapped: mapAssetToFrontend(data),
        reviewSnapshot
      });
    } catch (error) {
      const msg = error.response?.data?.message || error.message || 'Failed to load asset';
      setAssetModal((m) => ({ ...m, loading: false, error: msg }));
      toast.error(msg);
    }
  };

  const closeAssetModal = () => {
    setAssetModal({ open: false, loading: false, error: null, mapped: null, reviewSnapshot: null });
  };

  const closeBulkModal = () => {
    setBulkModal({ open: false, loading: false, error: null, bulk: null, reviewSnapshot: null });
  };

  const openBulkRegistrationInfo = async (reviewSnapshot) => {
    const bid = reviewBulkRegistrationId(reviewSnapshot);
    if (!bid) {
      toast.error('No bulk registration id on this review.');
      return;
    }
    let br = nestedBulkRegistration(reviewSnapshot);
    const members = bulkAssetsList(br);
    if (!br || members.length === 0) {
      setBulkModal({ open: true, loading: true, error: null, bulk: null, reviewSnapshot });
      try {
        const { data } = await assetBulkRegistrationsApi.getById(bid);
        setBulkModal({
          open: true,
          loading: false,
          error: null,
          bulk: data,
          reviewSnapshot
        });
      } catch (error) {
        const msg = error.response?.data?.message || error.message || 'Failed to load bulk registration';
        setBulkModal((m) => ({ ...m, loading: false, error: msg }));
        toast.error(msg);
      }
      return;
    }
    setBulkModal({ open: true, loading: false, error: null, bulk: br, reviewSnapshot });
  };

  const pickAssignableReviewer = (r) => {
    setAssignReviewerId(r.userId);
    setAssignReviewerQuery(reviewerPrimaryLabel(r));
    setAssignReviewerMenuOpen(false);
  };

  const handleAssign = async () => {
    if (!selectedId || !assignReviewerId.trim()) {
      toast.error('Search and select a reviewer from the list');
      return;
    }

    try {
      await legalReviewsApi.assign(selectedId, { reviewerUserId: assignReviewerId.trim() });
      toast.success('Reviewer assigned');
      setAssignReviewerId('');
      setAssignReviewerQuery('');
      await loadList();
      await loadDetail(selectedId);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    }
  };

  const handleApprove = async () => {
    if (!selectedId) return;
    try {
      await legalReviewsApi.approve(selectedId, { decisionNotes: decisionNotes.trim() || 'Approved' });
      toast.success('Legal review approved');
      setDecisionNotes('');
      await loadList();
      await loadDetail(selectedId);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    }
  };

  const handleReject = async () => {
    if (!selectedId) return;
    if (!decisionNotes.trim()) {
      toast.error('Add rejection notes');
      return;
    }
    try {
      await legalReviewsApi.reject(selectedId, { decisionNotes: decisionNotes.trim() });
      toast.success('Legal review rejected');
      setDecisionNotes('');
      await loadList();
      await loadDetail(selectedId);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    }
  };

  const handleComment = async () => {
    if (!selectedId || !newComment.trim()) return;
    try {
      await legalReviewsApi.addComment(selectedId, { comment: newComment.trim() });
      toast.success('Comment added');
      setNewComment('');
      await loadDetail(selectedId);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    }
  };

  if (!signedIn) {
    return (
      <Page>
        <Hero>
          <h1>
            <FiShield style={{ verticalAlign: 'middle', marginRight: '0.35rem' }} />
            Legal review
          </h1>
          <p>Sign in to access legal review APIs backed by the Core Tokenizer service.</p>
        </Hero>
        <Notice>Issuer Login uses the same JWT as the legal reviewer and admin roles.</Notice>
      </Page>
    );
  }

  if (!canAccessLegalReviewApi) {
    return (
      <Page>
        <Hero>
          <h1>Legal review</h1>
          <p>
            Your account needs the LegalReviewer, Admin, or Auditor role to call{' '}
            <code>/api/legal-reviews</code>.
          </p>
        </Hero>
        <Notice>Ask an administrator to assign the correct role to your user in the identity store.</Notice>
      </Page>
    );
  }

  const renderAssetModal = () => {
    const { open, loading, error, mapped, reviewSnapshot } = assetModal;
    const meta = mapped?.metadata || {};
    const docs = mapped?.documents || [];

    return (
      <AnimatePresence>
        {open && (
          <ModalOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeAssetModal}>
            <ModalCard
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <h2>Asset details</h2>
                <IconOnlyButton type="button" onClick={closeAssetModal} aria-label="Close">
                  <FiClose />
                </IconOnlyButton>
              </ModalHeader>
              <ModalBody>
                {loading && <p style={{ color: '#a0a0a0' }}>Loading asset…</p>}
                {error && !loading && <p style={{ color: '#ff8a8a' }}>{error}</p>}
                {!loading && !error && mapped && (
                  <>
                    <DetailGrid>
                      <dt>Title</dt>
                      <dd>{mapped.name || '—'}</dd>
                      <dt>Asset #</dt>
                      <dd>{mapped.assetNumber || '—'}</dd>
                      <dt>Category / sector</dt>
                      <dd>{formatSectorLabel(mapped.sector)}</dd>
                      <dt>API sector</dt>
                      <dd>{mapped.sector || '—'}</dd>
                      <dt>Status</dt>
                      <dd>{formatEnumLabel(mapped.status)}</dd>
                      <dt>Country</dt>
                      <dd>{mapped.country || '—'}</dd>
                      <dt>Valuation (USD)</dt>
                      <dd>
                        {mapped.valuationUSD != null
                          ? Number(mapped.valuationUSD).toLocaleString(undefined, { maximumFractionDigits: 2 })
                          : '—'}
                      </dd>
                      <dt>Legal owner</dt>
                      <dd>{mapped.custodian || '—'}</dd>
                      <dt>Owner identifier</dt>
                      <dd style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{mapped.ownerAddress || '—'}</dd>
                      <dt>Location</dt>
                      <dd>{mapped.location || '—'}</dd>
                      <dt>Asset class ID</dt>
                      <dd style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{mapped.assetClassId || '—'}</dd>
                      <dt>Tokenizer ID</dt>
                      <dd style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{mapped.authorizedTokenizerId || '—'}</dd>
                      <dt>Asset ID</dt>
                      <dd style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{mapped.id}</dd>
                    </DetailGrid>

                    {reviewSnapshot && (
                      <>
                        <h3 style={{ fontSize: '1rem', margin: '0 0 0.75rem', color: '#9bd6ff' }}>Legal review (snapshot)</h3>
                        <DetailGrid>
                          <dt>Review status</dt>
                          <dd>{formatEnumLabel(reviewSnapshot.status)}</dd>
                          <dt>Assigned reviewer</dt>
                          <dd style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {reviewSnapshot.assignedReviewerId || reviewSnapshot.AssignedReviewerId || '—'}
                          </dd>
                          <dt>Decision notes</dt>
                          <dd>{reviewSnapshot.decisionNotes || reviewSnapshot.DecisionNotes || '—'}</dd>
                        </DetailGrid>
                      </>
                    )}

                    <h3 style={{ fontSize: '1rem', margin: '1rem 0 0.5rem', color: '#9bd6ff' }}>Metadata draft</h3>
                    <JsonBlock>{JSON.stringify(meta, null, 2)}</JsonBlock>

                    <h3 style={{ fontSize: '1rem', margin: '1rem 0 0.5rem', color: '#9bd6ff' }}>
                      Documents ({docs.length})
                    </h3>
                    {docs.length === 0 ? (
                      <p style={{ color: '#a0a0a0', fontSize: '0.9rem' }}>No documents on file.</p>
                    ) : (
                      <DocTable>
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>File</th>
                            <th>IPFS CID</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {docs.map((d) => {
                            const cid = String(d.ipfsCid || d.IpfsCid || '').trim();
                            const rowKey = assetDocumentRowKey(d);
                            return (
                              <tr key={rowKey}>
                                <td>{d.documentType || d.DocumentType || '—'}</td>
                                <td>{d.fileName || d.FileName || '—'}</td>
                                <td style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>
                                  {cid || '—'}
                                </td>
                                <td className="doc-actions">
                                  <DocViewButton
                                    type="button"
                                    disabled={!cid || openingDocumentKey === rowKey}
                                    onClick={() => openLegalDocument(d)}
                                    title={cid ? 'Open document in a new tab' : 'No CID'}
                                  >
                                    <FiEye />
                                    {openingDocumentKey === rowKey ? 'Opening…' : 'View'}
                                  </DocViewButton>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </DocTable>
                    )}
                  </>
                )}
              </ModalBody>
            </ModalCard>
          </ModalOverlay>
        )}
      </AnimatePresence>
    );
  };

  return (
    <Page>
      <Hero>
        <h1>Legal review queue</h1>
        <p>
          Filter the queue by review status, then search by asset title, sector, program, tokenizer, country, or id. Use{' '}
          <strong>Full asset</strong> or <strong>Bulk registration</strong> for documents and full metadata.
        </p>
      </Hero>

      <Toolbar>
        <Button type="button" onClick={loadList} disabled={loading}>
          <FiRefreshCw />
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
        {canAccessLegalReviewApi && (
          <Link to="/legal-firm/assets" style={{ textDecoration: 'none' }}>
            <Button type="button" $variant="secondary">
              <FiBriefcase />
              Firm legal assets
            </Button>
          </Link>
        )}
        {isAuditor && !canAssignOrDecideLegalReview && (
          <Notice style={{ margin: 0, flex: '1 1 280px' }}>
            Auditor: you can view reviews and add comments; assign / approve / reject require LegalReviewer or Admin.
          </Notice>
        )}
      </Toolbar>

      <Layout>
        <Panel>
          <h3
            style={{
              marginTop: 0,
              marginBottom: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.75rem',
              flexWrap: 'wrap'
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FiShield />
              Queue
              <StatusPill style={{ fontSize: '0.8rem', padding: '0.15rem 0.55rem' }}>
                {reviewsForStatusTab.length}
              </StatusPill>
            </span>
            {(queueFilter.trim() || queueStatusTab !== 'all') && (
              <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                {queueFilter.trim()
                  ? `Showing ${filteredReviews.length} of ${reviewsForStatusTab.length}${
                      queueStatusTab === 'all' ? '' : ' in this tab'
                    }`
                  : `${reviewsForStatusTab.length} of ${reviews.length} total`}
              </span>
            )}
          </h3>

          <QueueTabBar role="tablist" aria-label="Queue by review status">
            {queueStatusTabs.map((tab) => (
              <QueueTab
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={queueStatusTab === tab.id}
                $active={queueStatusTab === tab.id}
                onClick={() => setQueueStatusTab(tab.id)}
              >
                {tab.label}
                <QueueTabCount $active={queueStatusTab === tab.id}>{queueStatusCounts[tab.id] ?? 0}</QueueTabCount>
              </QueueTab>
            ))}
          </QueueTabBar>

          <SearchWrap>
            <FiSearch aria-hidden />
            <input
              type="search"
              placeholder="Search by title, sector, program, tokenizer, country, or id…"
              value={queueFilter}
              onChange={(e) => setQueueFilter(e.target.value)}
              aria-label="Filter queue"
            />
          </SearchWrap>

          {reviews.length === 0 && !loading && (
            <EmptyQueue>
              <strong>Nothing in the queue yet</strong>
              When tokenizers submit assets for legal review, they will appear here with sector, program, and document
              counts.
            </EmptyQueue>
          )}

          {reviews.length > 0 && reviewsForStatusTab.length === 0 && queueStatusTab !== 'all' && (
            <EmptyQueue>
              <strong>No reviews in this status</strong>
              Choose another tab — this filter has no cases right now.
            </EmptyQueue>
          )}

          {reviews.length > 0 &&
            reviewsForStatusTab.length > 0 &&
            filteredReviews.length === 0 &&
            queueFilter.trim() && (
              <EmptyQueue>
                <strong>No matches</strong>
                Try a different search term, or clear the search to see all {reviewsForStatusTab.length} case
                {reviewsForStatusTab.length === 1 ? '' : 's'} in this tab.
              </EmptyQueue>
            )}

          <QueueScroll>
            {filteredReviews.map((r) => {
              const a = nestedAsset(r);
              const br = nestedBulkRegistration(r);
              const brFirst = br ? bulkAssetsList(br)[0] : null;
              const chipA = a || brFirst;
              const id = reviewIdStr(r);
              const aid = reviewAssetId(r);
              const bid = reviewBulkRegistrationId(r);
              const active = String(selectedId) === id;
              const meta = getReviewStatusMeta(r);
              const submitted = formatQueueTime(reviewCreatedAt(r));
              const docCount = br ? bulkDocCount(br) : documentsOf(a).length;
              const cardTitleText = a
                ? assetTitle(a)
                : br
                  ? `${bulkRegistrationTitle(br)} · ${bulkAssetsList(br).length} assets`
                  : `Review ${shortGuid(id)}`;

              return (
                <QueueCardRow key={id}>
                  <QueueCard
                    type="button"
                    $active={active}
                    $accent={meta.accent}
                    onClick={() => setSelectedId(id)}
                    aria-current={active ? 'true' : undefined}
                  >
                    <CardInner>
                      <CardHeader>
                        <CardTitle>{cardTitleText}</CardTitle>
                        <StatusBadge $tone={meta.tone}>{meta.label}</StatusBadge>
                      </CardHeader>

                      {(chipA || br) && (
                        <Chips>
                          <Chip title="Sector / category">
                            <FiLayers aria-hidden />
                            {formatSectorLabel(
                              chipA ? assetSectorRaw(chipA) : pick(br, 'sector', 'Sector')
                            )}
                          </Chip>
                          {assetClassName(chipA) && (
                            <Chip title="Asset program / class">
                              <FiBriefcase aria-hidden />
                              {assetClassName(chipA)}
                            </Chip>
                          )}
                          {(chipA ? assetCountry(chipA) : pick(br, 'country', 'Country')) && (
                            <Chip title="Country">
                              <FiMapPin aria-hidden />
                              {chipA ? assetCountry(chipA) : pick(br, 'country', 'Country')}
                            </Chip>
                          )}
                          {assetNumber(chipA) && !br && (
                            <Chip title="Asset number">
                              <FiHash aria-hidden />
                              {assetNumber(chipA)}
                            </Chip>
                          )}
                          {br && (
                            <Chip title="Bulk batch">
                              <FiPackage aria-hidden />
                              Bulk registration
                            </Chip>
                          )}
                          <Chip title="Documents uploaded">
                            <FiFileText aria-hidden />
                            {docCount} document{docCount === 1 ? '' : 's'}
                          </Chip>
                          {chipA && assetStatus(chipA) && (
                            <Chip title="Asset workflow status">
                              <FiUser aria-hidden />
                              {formatEnumLabel(assetStatus(chipA))}
                            </Chip>
                          )}
                          {tokenizerOrg(chipA) && (
                            <Chip title="Authorized tokenizer">
                              <FiKey aria-hidden />
                              {tokenizerOrg(chipA)}
                            </Chip>
                          )}
                        </Chips>
                      )}

                      <CardFooter>
                        {submitted && (
                          <FooterHint>
                            <FiClock aria-hidden />
                            Submitted {submitted}
                          </FooterHint>
                        )}
                        <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.7rem', color: '#64748b' }}>
                          Review {shortGuid(id)}
                          {aid ? ` · Asset ${shortGuid(aid)}` : ''}
                          {bid && !aid ? ` · Bulk ${shortGuid(bid)}` : ''}
                        </span>
                      </CardFooter>
                    </CardInner>
                  </QueueCard>
                  <IconOnlyButton
                    type="button"
                    title={
                      aid
                        ? 'Open full asset details'
                        : bid
                          ? 'Open bulk registration and member assets'
                          : 'No linked asset or bulk registration'
                    }
                    aria-label={
                      aid
                        ? 'Open full asset details'
                        : bid
                          ? 'Open bulk registration and member assets'
                          : 'No linked asset or bulk registration'
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (aid) openAssetFullInfo(aid, r);
                      else if (bid) openBulkRegistrationInfo(r);
                      else toast.error('No asset or bulk registration on this review.');
                    }}
                  >
                    <FiInfo />
                  </IconOnlyButton>
                </QueueCardRow>
              );
            })}
          </QueueScroll>
        </Panel>

        <Panel>
          {!selectedId && <p style={{ color: '#a0a0a0' }}>Select a review to load detail and actions.</p>}
          {selectedId && (
            <>
              <h3 style={{ marginTop: 0 }}>Review detail</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                {(reviewAssetId(detail) || reviewAssetId(selectedSummary)) && (
                  <Button
                    type="button"
                    $variant="secondary"
                    onClick={() => {
                      const aid = reviewAssetId(detail) || reviewAssetId(selectedSummary);
                      if (aid) openAssetFullInfo(aid, detail || selectedSummary);
                      else toast.error('No asset id on this review.');
                    }}
                  >
                    <FiInfo />
                    Full asset info
                  </Button>
                )}
                {(reviewBulkRegistrationId(detail) || reviewBulkRegistrationId(selectedSummary)) && (
                  <Button
                    type="button"
                    $variant="secondary"
                    onClick={() => openBulkRegistrationInfo(detail || selectedSummary)}
                  >
                    <FiPackage />
                    Bulk registration &amp; members
                  </Button>
                )}
              </div>

              {selectedAsset && (
                <DetailGrid style={{ marginBottom: '1rem' }}>
                  <dt>Asset title</dt>
                  <dd>{assetTitle(selectedAsset)}</dd>
                  <dt>Sector</dt>
                  <dd>{formatSectorLabel(assetSectorRaw(selectedAsset))}</dd>
                  <dt>Program / class</dt>
                  <dd>{assetClassName(selectedAsset) || '—'}</dd>
                  <dt>Tokenizer</dt>
                  <dd>{tokenizerOrg(selectedAsset) || '—'}</dd>
                  <dt>Asset #</dt>
                  <dd>{assetNumber(selectedAsset) || '—'}</dd>
                  <dt>Country</dt>
                  <dd>{assetCountry(selectedAsset) || '—'}</dd>
                  <dt>Asset status</dt>
                  <dd>{formatEnumLabel(assetStatus(selectedAsset))}</dd>
                </DetailGrid>
              )}

              {isBulkLegalReview(detail || selectedSummary) && selectedBulk && (
                <DetailGrid style={{ marginBottom: '1rem' }}>
                  <dt>Bulk title</dt>
                  <dd>{bulkRegistrationTitle(selectedBulk)}</dd>
                  <dt>Members</dt>
                  <dd>{bulkAssetsList(selectedBulk).length}</dd>
                  <dt>Documents (all members)</dt>
                  <dd>{bulkDocCount(selectedBulk)}</dd>
                </DetailGrid>
              )}

              <p style={{ color: '#a0a0a0', fontSize: '0.9rem', wordBreak: 'break-all' }}>
                {(reviewAssetId(detail) || reviewAssetId(selectedSummary)) ? (
                  <>
                    Asset ID:{' '}
                    <strong style={{ color: '#fff' }}>{reviewAssetId(detail) || reviewAssetId(selectedSummary)}</strong>
                  </>
                ) : (reviewBulkRegistrationId(detail) || reviewBulkRegistrationId(selectedSummary)) ? (
                  <>
                    Bulk registration ID:{' '}
                    <strong style={{ color: '#fff' }}>
                      {reviewBulkRegistrationId(detail) || reviewBulkRegistrationId(selectedSummary)}
                    </strong>
                  </>
                ) : (
                  <>
                    Linked ID: <strong style={{ color: '#fff' }}>—</strong>
                  </>
                )}
              </p>
              <p style={{ color: '#a0a0a0', fontSize: '0.9rem' }}>
                Review status:{' '}
                <StatusPill>{formatEnumLabel(detail?.status || selectedSummary?.status)}</StatusPill>
              </p>
              {(detail?.assignedReviewerId || detail?.AssignedReviewerId) && (
                <p style={{ color: '#a0a0a0', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                  Assigned reviewer:{' '}
                  <strong style={{ color: '#fff' }}>{detail?.assignedReviewerId || detail?.AssignedReviewerId}</strong>
                </p>
              )}

              <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '1.25rem' }}>
                <FiMessageSquare />
                Comments
              </h4>
              <CommentList>
                {(detail?.comments || []).map((c) => (
                  <li key={c.id}>
                    {c.comment}
                    <div className="meta">
                      By {c.createdByUserId} · {c.createdAtUtc ? new Date(c.createdAtUtc).toLocaleString() : ''}
                    </div>
                  </li>
                ))}
              </CommentList>

              <Field>
                <label>New comment</label>
                <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Comment text" />
                <Button type="button" style={{ marginTop: '0.5rem' }} onClick={handleComment}>
                  Add comment
                </Button>
              </Field>

              {canAssignOrDecideLegalReview && (
                <>
                  <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '1rem' }}>
                    <FiUserPlus />
                    Assign reviewer
                  </h4>
                  <Field ref={reviewerPickerRef}>
                    <label htmlFor="legal-assign-reviewer-search">Reviewer</label>
                    <ReviewerPickerWrap>
                      <input
                        id="legal-assign-reviewer-search"
                        type="search"
                        autoComplete="off"
                        value={assignReviewerQuery}
                        onChange={(e) => {
                          setAssignReviewerQuery(e.target.value);
                          setAssignReviewerId('');
                          setAssignReviewerMenuOpen(true);
                        }}
                        onFocus={() => setAssignReviewerMenuOpen(true)}
                        placeholder={
                          reviewersLoading
                            ? 'Loading reviewers…'
                            : 'Search by name, email, or organization…'
                        }
                        disabled={reviewersLoading}
                        aria-expanded={assignReviewerMenuOpen}
                        aria-controls="legal-assign-reviewer-list"
                        aria-autocomplete="list"
                      />
                      {assignReviewerMenuOpen && !reviewersLoading && (
                        <ReviewerDropdown id="legal-assign-reviewer-list" role="listbox">
                          {filteredAssignableReviewers.length === 0 ? (
                            <ReviewerOption as="li" style={{ cursor: 'default' }}>
                              <span className="name">
                                {assignableReviewers.length === 0
                                  ? 'No assignable reviewers'
                                  : 'No matches — try another search'}
                              </span>
                            </ReviewerOption>
                          ) : (
                            filteredAssignableReviewers.map((r) => (
                              <ReviewerOption
                                key={r.userId}
                                role="option"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => pickAssignableReviewer(r)}
                              >
                                <div className="name">{reviewerPrimaryLabel(r)}</div>
                                {r.organizationName ? (
                                  <div className="sub">{r.organizationName}</div>
                                ) : null}
                              </ReviewerOption>
                            ))
                          )}
                        </ReviewerDropdown>
                      )}
                    </ReviewerPickerWrap>
                    <ReviewerHint>
                      {assignableReviewers.length > 0
                        ? `${assignableReviewers.length} reviewer${assignableReviewers.length === 1 ? '' : 's'} you can assign.`
                        : !reviewersLoading
                          ? 'No reviewers available for your account.'
                          : null}
                    </ReviewerHint>
                    <Button type="button" style={{ marginTop: '0.5rem' }} $variant="secondary" onClick={handleAssign}>
                      Assign
                    </Button>
                  </Field>

                  <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '1rem' }}>
                    <FiUser />
                    Decision
                  </h4>
                  <Field>
                    <label>Notes (required for reject)</label>
                    <textarea
                      value={decisionNotes}
                      onChange={(e) => setDecisionNotes(e.target.value)}
                      placeholder="Decision notes for audit trail"
                    />
                  </Field>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <Button type="button" onClick={handleApprove}>
                      <FiCheck />
                      Approve
                    </Button>
                    <Button type="button" $variant="secondary" onClick={handleReject}>
                      <FiX />
                      Reject
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </Panel>
      </Layout>

      {renderAssetModal()}
      <BulkRegistrationReviewModal
        bulkModalState={bulkModal}
        onClose={closeBulkModal}
        onOpenMemberAsset={(assetId, snapshot) => {
          closeBulkModal();
          openAssetFullInfo(assetId, snapshot);
        }}
      />
    </Page>
  );
};

export default LegalReviewCenter;
